import React, { useState } from 'react';
import './Contact.css';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xnjlnrwa';

const IconEmail = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect width="20" height="16" x="2" y="4" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const IconCalendar = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 2v4"/><path d="M16 2v4"/>
    <rect width="18" height="18" x="3" y="4" rx="2"/>
    <path d="M3 10h18"/>
  </svg>
);

const IconClock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const IconMapPin = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m22 2-7 20-4-9-9-4Z"/>
    <path d="M22 2 11 13"/>
  </svg>
);

function Contact({ onBack }) {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name:    formData.name,
          email:   formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">

      {/* NAV */}
      <nav className="contact-nav">
        <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer" className="contact-nav__logo">
          <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" />
        </a>
        <button type="button" className="contact-nav__back" onClick={onBack}>
          ← Dashboard
        </button>
      </nav>

      {/* HERO */}
      <section className="contact-hero">
        <p className="contact-eyebrow">NextLeader Coaching</p>
        <h1>Get in Touch</h1>
        <p className="contact-hero__sub">
          Ready for a conversation? We'd love to hear from you — whether you have questions about coaching, want to schedule a discovery call, or need support.
        </p>
      </section>

      {/* MAIN */}
      <section className="contact-section">
        <div className="contact-container">

          {/* LEFT — info */}
          <div className="contact-info-col">
            <p className="contact-label">How to reach us</p>
            <h2>We're here for you</h2>

            <div className="contact-info-cards">
              <div className="contact-info-card">
                <div className="contact-info-card__icon"><IconEmail /></div>
                <div className="contact-info-card__text">
                  <h3>Email</h3>
                  <a href="mailto:irwin@nextleadercoaching.com">irwin@nextleadercoaching.com</a>
                  <span className="contact-info-card__note">Response within 24 hours</span>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-card__icon"><IconCalendar /></div>
                <div className="contact-info-card__text">
                  <h3>Book a Discovery Call</h3>
                  <p>Let's discuss your leadership goals</p>
                  <a
                    href="https://calendar.app.google/cPuTHDHbHgVoRKbNA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-info-card__link"
                  >
                    Schedule a free consultation →
                  </a>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-card__icon"><IconClock /></div>
                <div className="contact-info-card__text">
                  <h3>Office Hours</h3>
                  <p>Monday – Friday: 9 AM – 6 PM PST</p>
                  <p>Saturday: 10 AM – 2 PM PST</p>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-card__icon"><IconMapPin /></div>
                <div className="contact-info-card__text">
                  <h3>Location</h3>
                  <p>Alameda, California</p>
                  <p>Serving clients nationwide</p>
                </div>
              </div>
            </div>

            <div className="contact-quote">
              <blockquote>
                "My coaching alliances aim to help individuals 'lift the fog' on their objectives and overcome any limitations to achieve their goals."
              </blockquote>
              <cite>— Irwin, NextLeader Coach</cite>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="contact-form-col">
            <p className="contact-label">Send a message</p>
            <h2>Reach out directly</h2>

            <div className="contact-form-card">
              {submitStatus === 'success' && (
                <div className="contact-alert contact-alert--success">
                  <strong>Message sent.</strong> Thank you for reaching out — we'll be in touch within 24 hours.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="contact-alert contact-alert--error">
                  <strong>Something went wrong.</strong> Please try again or email us directly at{' '}
                  <a href="mailto:irwin@nextleadercoaching.com">irwin@nextleadercoaching.com</a>.
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="contact-form__row">
                  <div className="contact-form__group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="contact-form__group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="contact-form__group">
                  <label htmlFor="subject">Topic</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a topic</option>
                    <option value="discovery-call">Book a Discovery Call</option>
                    <option value="coaching-services">Coaching Services</option>
                    <option value="platform-support">Platform Support</option>
                    <option value="billing-payment">Billing &amp; Payment</option>
                    <option value="general-inquiry">General Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="contact-form__group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="6"
                    placeholder="How can we help you?"
                  />
                  <span className="contact-form__charcount">{formData.message.length} / 1000</span>
                </div>

                <button type="submit" className="contact-submit-btn" disabled={isSubmitting}>
                  {isSubmitting
                    ? <><span className="contact-spinner" /> Sending…</>
                    : <><IconSend /> Send Message</>
                  }
                </button>
              </form>

              <p className="contact-form__privacy">
                Your information is kept confidential and used only to respond to your inquiry.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* CTA BAND */}
      <section className="contact-cta-band">
        <h2>Ready to lift the fog?</h2>
        <p>Curious about coaching or looking to set up a complimentary discovery call?</p>
        <a
          href="https://calendar.app.google/cPuTHDHbHgVoRKbNA"
          target="_blank"
          rel="noopener noreferrer"
          className="contact-cta-btn"
        >
          Book a Discovery Call
        </a>
      </section>

      {/* FOOTER */}
      <footer className="contact-footer">
        <div className="contact-footer__inner">
          <div className="contact-footer__brand">
            <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="contact-footer__logo" />
            <p>Quiet, intentional coaching for leaders in transition. Bridging where you are with where you want to be.</p>
          </div>
          <div>
            <h4>Navigation</h4>
            <ul>
              <li><button type="button" onClick={onBack}>← Dashboard</button></li>
              <li><a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">Website</a></li>
              <li><a href="https://www.nextleadercoaching.com/coaching-services.html" target="_blank" rel="noopener noreferrer">Coaching Services</a></li>
              <li><a href="https://www.nextleadercoaching.com/about.html" target="_blank" rel="noopener noreferrer">About Irwin</a></li>
            </ul>
          </div>
          <div>
            <h4>Contact</h4>
            <p><a href="mailto:irwin@nextleadercoaching.com">irwin@nextleadercoaching.com</a></p>
            <p>Alameda, California</p>
            <a
              href="https://calendar.app.google/cPuTHDHbHgVoRKbNA"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-footer__cta"
            >
              Book a Consultation
            </a>
          </div>
        </div>
        <div className="contact-footer__bottom">
          <span>© {new Date().getFullYear()} NextLeader Coaching. All rights reserved.</span>
          <span>Alameda, California</span>
        </div>
      </footer>

    </div>
  );
}

export default Contact;
